import { ClipboardList, HandCoins, PencilLine, RotateCcw, XCircle } from 'lucide-react';
import { ActionIconButton, TableEmptyOverlay } from '../common';

function SalesOrderTable({
  salesOrders,
  onEditSalesOrder,
  onDeleteSalesOrder,
  onReopenSalesOrder,
  onUpdateBilling,
  includeCustomerBranch = false,
  includeSeaweedType = false,
  includeCommercialFields = false,
  includeContainerDetails = true,
  includeWarehouseLocation = true,
}) {
  return (
    <div className="relative overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[var(--theme-accent-panel)]">
              <TableHeading>Batch No.</TableHeading>
              <TableHeading>S.O. Date</TableHeading>
              <TableHeading>Customer</TableHeading>
              {includeCustomerBranch ? <TableHeading>Delivery Location</TableHeading> : null}
              {includeSeaweedType ? <TableHeading>Seaweed Type</TableHeading> : null}
              {includeCommercialFields ? (
                <TableHeading>FCL Quantity</TableHeading>
              ) : null}
              {includeContainerDetails ? (
                <>
                  <TableHeading>FCL</TableHeading>
                  <TableHeading>Code #</TableHeading>
                  <TableHeading>Cont. #</TableHeading>
                </>
              ) : null}
              {includeWarehouseLocation ? <TableHeading>Warehouse</TableHeading> : null}
              {includeContainerDetails ? (
                <>
                  <TableHeading>Seaweeds</TableHeading>
                  <TableHeading>Supplier</TableHeading>
                  <TableHeading>Sacks</TableHeading>
                </>
              ) : null}
              <TableHeading>Status</TableHeading>
              <TableHeading>Payment</TableHeading>
              <TableHeading>Action</TableHeading>
            </tr>
          </thead>
          <tbody>
            {salesOrders.length ? (
              salesOrders.map((salesOrder) => {
                const canModify = canModifySalesOrder(salesOrder);
                const canReopen = canReopenSalesOrder(salesOrder);

                return (
                <tr key={salesOrder.id} className="border-t border-[var(--theme-border-soft)] transition-colors hover:bg-[var(--theme-surface-soft)]">
                  {/* Photo column removed */}
                  <TableCell>{formatSalesOrderCode(salesOrder)}</TableCell>
                  <TableCell>{formatDate(salesOrder.soDate)}</TableCell>
                  <TableCell>{salesOrder.customerName}</TableCell>
                  {includeCustomerBranch ? <TableCell>{salesOrder.branch || '-'}</TableCell> : null}
                  {includeSeaweedType ? (
                    <TableCell>{getSeaweedTypeLabel(salesOrder)}</TableCell>
                  ) : null}
                  {includeCommercialFields ? (
                    <TableCell>{formatQuantityWithUnit(salesOrder.quantity)}</TableCell>
                  ) : null}
                  {includeContainerDetails ? (
                    <>
                      <TableCell>{salesOrder.fclBatchNumber}</TableCell>
                      <TableCell>{salesOrder.codeNumber}</TableCell>
                      <TableCell>{salesOrder.containerNumber}</TableCell>
                    </>
                  ) : null}
                  {includeWarehouseLocation ? (
                    <TableCell>{salesOrder.warehouseLocation}</TableCell>
                  ) : null}
                  {includeContainerDetails ? (
                    <>
                      <TableCell>{salesOrder.sourceContainerLabel}</TableCell>
                      <TableCell>{salesOrder.supplierName}</TableCell>
                      <TableCell>{salesOrder.sacksMark}</TableCell>
                    </>
                  ) : null}
                  <TableCell>
                    <SalesOrderStatusBadge status={salesOrder.status} />
                  </TableCell>
                  <TableCell>
                    <BillingStatusBadge
                      status={salesOrder.billingStatus}
                      isExpired={salesOrder.isBillingProofExpired}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {onUpdateBilling ? (
                        <ActionIconButton
                          onClick={() => onUpdateBilling(salesOrder)}
                          label={`Update payment status for ${formatSalesOrderCode(salesOrder)}`}
                          disabled={String(salesOrder.status || '') === 'Cancelled'}
                        >
                          <HandCoins className="h-4 w-4" aria-hidden="true" />
                        </ActionIconButton>
                      ) : null}
                      <ActionIconButton
                        onClick={() => onEditSalesOrder(salesOrder)}
                        label={`Edit sales order ${salesOrder.id}`}
                        disabled={!canModify}
                      >
                        <PencilLine className="h-4 w-4" aria-hidden="true" />
                      </ActionIconButton>
                      {onDeleteSalesOrder ? (
                        <ActionIconButton
                          onClick={() => onDeleteSalesOrder(salesOrder)}
                          label={`Cancel sales order ${salesOrder.id}`}
                          tone="danger"
                          disabled={!canModify}
                        >
                          <XCircle className="h-4 w-4" aria-hidden="true" />
                        </ActionIconButton>
                      ) : null}
                      {onReopenSalesOrder && canReopen ? (
                        <ActionIconButton
                          onClick={() => onReopenSalesOrder(salesOrder)}
                          label={`Reopen sales order ${formatSalesOrderCode(salesOrder)}`}
                        >
                          <RotateCcw className="h-4 w-4" aria-hidden="true" />
                        </ActionIconButton>
                      ) : null}
                    </div>
                  </TableCell>
                </tr>
                );
              })
            ) : (
              <TableEmptyOverlay.Row
                colSpan={getSalesOrderTableColumnCount(
                  includeCustomerBranch,
                  includeSeaweedType,
                  includeCommercialFields,
                  includeContainerDetails,
                  includeWarehouseLocation
                )}
              />
            )}
          </tbody>
        </table>
      </div>
      {!salesOrders.length ? (
        <TableEmptyOverlay headerOffsetClassName="top-[50px]">
          <EmptyState />
        </TableEmptyOverlay>
      ) : null}

      <div className="grid gap-3 p-4 md:hidden">
        {salesOrders.length ? (
          salesOrders.map((salesOrder) => {
            const canModify = canModifySalesOrder(salesOrder);
            const canReopen = canReopenSalesOrder(salesOrder);

            return (
            <article
              key={salesOrder.id}
              className="rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-3 transition duration-150 hover:-translate-y-0.5 hover:bg-[var(--theme-surface-soft)] hover:shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.11)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <strong className="block text-[1rem] text-[var(--theme-text)]">
                    {formatSalesOrderCode(salesOrder)}
                  </strong>
                  <span className="mt-1 block text-[0.92rem] text-[var(--theme-text-muted)]">
                    {salesOrder.customerName} - {formatDate(salesOrder.soDate)}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {onUpdateBilling ? (
                    <ActionIconButton
                      onClick={() => onUpdateBilling(salesOrder)}
                      label={`Update payment status for ${formatSalesOrderCode(salesOrder)}`}
                      disabled={String(salesOrder.status || '') === 'Cancelled'}
                    >
                      <HandCoins className="h-4 w-4" aria-hidden="true" />
                    </ActionIconButton>
                  ) : null}
                  <ActionIconButton
                    onClick={() => onEditSalesOrder(salesOrder)}
                    label={`Edit sales order ${salesOrder.id}`}
                    disabled={!canModify}
                  >
                    <PencilLine className="h-4 w-4" aria-hidden="true" />
                  </ActionIconButton>
                  {onDeleteSalesOrder ? (
                    <ActionIconButton
                      onClick={() => onDeleteSalesOrder(salesOrder)}
                      label={`Cancel sales order ${salesOrder.id}`}
                      tone="danger"
                      disabled={!canModify}
                    >
                      <XCircle className="h-4 w-4" aria-hidden="true" />
                    </ActionIconButton>
                  ) : null}
                  {onReopenSalesOrder && canReopen ? (
                    <ActionIconButton
                      onClick={() => onReopenSalesOrder(salesOrder)}
                      label={`Reopen sales order ${formatSalesOrderCode(salesOrder)}`}
                    >
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    </ActionIconButton>
                  ) : null}
                </div>
              </div>

              <div className="mt-3 grid gap-1.5 text-[0.88rem] text-[var(--theme-text-subtle)]">
                <SalesOrderMeta label="Batch No." value={formatSalesOrderCode(salesOrder)} />
                {includeCustomerBranch ? (
                  <SalesOrderMeta label="Delivery Location" value={salesOrder.branch || '-'} />
                ) : null}
                {includeSeaweedType ? (
                  <SalesOrderMeta label="Seaweed Type" value={getSeaweedTypeLabel(salesOrder)} />
                ) : null}
                {includeCommercialFields ? (
                  <SalesOrderMeta
                    label="FCL Quantity"
                    value={formatQuantityWithUnit(salesOrder.quantity)}
                  />
                ) : null}
                {includeContainerDetails ? (
                  <>
                    <SalesOrderMeta label="FCL" value={salesOrder.fclBatchNumber} />
                    <SalesOrderMeta label="Code #" value={salesOrder.codeNumber} />
                    <SalesOrderMeta label="Cont. #" value={salesOrder.containerNumber} />
                  </>
                ) : null}
                {includeWarehouseLocation ? (
                  <SalesOrderMeta label="Warehouse" value={salesOrder.warehouseLocation} />
                ) : null}
                {includeContainerDetails ? (
                  <>
                    <SalesOrderMeta label="Seaweeds" value={salesOrder.sourceContainerLabel} />
                    <SalesOrderMeta label="Supplier" value={salesOrder.supplierName} />
                    <SalesOrderMeta label="Sacks" value={salesOrder.sacksMark} />
                  </>
                ) : null}
                <SalesOrderMeta
                  label="Status"
                  value={<SalesOrderStatusBadge status={salesOrder.status} />}
                />
                <SalesOrderMeta
                  label="Payment"
                  value={
                    <BillingStatusBadge
                      status={salesOrder.billingStatus}
                      isExpired={salesOrder.isBillingProofExpired}
                    />
                  }
                />
              </div>
            </article>
            );
          })
        ) : (
          <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
            <EmptyState />
          </div>
        )}
      </div>
    </div>
  );
}

function TableHeading({ children }) {
  return (
    <th className="h-[50px] whitespace-nowrap px-3 py-2.5 text-left align-middle text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[var(--theme-accent-panel-text)] lg:px-4">
      {children}
    </th>
  );
}

function TableCell({ children }) {
  return (
    <td className="whitespace-nowrap px-3 py-2 align-middle text-[0.84rem] text-[#222222] lg:px-4">
      {children}
    </td>
  );
}

function SalesOrderMeta({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--theme-text-muted)]">{label}</span>
      <div className="text-right font-medium text-[#2a2a2a]">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
        <ClipboardList className="h-6 w-6" aria-hidden="true" />
      </div>
      <strong className="mt-4 text-[1rem] font-medium text-[var(--theme-text-strong)]">
        No sales orders found
      </strong>
    </div>
  );
}

function getSalesOrderTableColumnCount(
  includeCustomerBranch,
  includeSeaweedType,
  includeCommercialFields,
  includeContainerDetails,
  includeWarehouseLocation
) {
  return (
    5 +
    (includeCustomerBranch ? 1 : 0) +
    (includeSeaweedType ? 1 : 0) +
    (includeCommercialFields ? 1 : 0) +
    (includeWarehouseLocation ? 1 : 0) +
    (includeContainerDetails ? 6 : 0) +
    1
  );
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatSalesOrderCode(salesOrder) {
  return salesOrder.soCode || `B-${String(salesOrder.id).padStart(3, '0')}`;
}

function formatQuantity(value) {
  const quantity = Number(value);

  if (!Number.isFinite(quantity)) {
    return '-';
  }

  return quantity.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatUnit() {
  return 'FCL';
}

function formatQuantityWithUnit(quantity) {
  return `${formatQuantity(quantity)} ${formatUnit()}`;
}

function getSeaweedTypeLabel(salesOrder) {
  return salesOrder.seaweedType || salesOrder.seaweedTypeName || '-';
}

function canModifySalesOrder(salesOrder) {
  return String(salesOrder.status || 'Active') === 'Active';
}

function canReopenSalesOrder(salesOrder) {
  return String(salesOrder.status || '') === 'Completed';
}

function SalesOrderStatusBadge({ status }) {
  const isCancelled = status === 'Cancelled';
  const isCompleted = status === 'Completed';

  return (
    <span
      className={`inline-flex min-w-[96px] items-center justify-center rounded-full px-3 py-1 text-[0.72rem] font-semibold uppercase tracking-[0.12em] ${
        isCancelled
          ? 'bg-[#f8e9e4] text-[#b85b48]'
          : isCompleted
            ? 'bg-[#e7f4e8] text-[#4f7b52]'
          : 'bg-[var(--theme-accent-soft)] text-[#b77924]'
      }`}
    >
      {status || 'Active'}
    </span>
  );
}

function BillingStatusBadge({ status, isExpired = false }) {
  const isPaid = status === 'Paid';

  return (
    <span
      className={`inline-flex min-w-[82px] items-center justify-center rounded-full px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.1em] ${
        isPaid
          ? 'bg-[#e7f4e8] text-[#4f7b52]'
          : 'bg-[#fff5dd] text-[#9a661c]'
      }`}
      title={isExpired ? 'Billing proof image expired' : undefined}
    >
      {isPaid ? 'Paid' : 'Unpaid'}
    </span>
  );
}

export default SalesOrderTable;
